/**
 * Ryan O'Dowd
 * 2023-07-14
 * © Copyright 2023 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  PRACTICE_ROUTE,
  READ_ROUTE,
  addToReadingHistory,
  updateWrinkle,
} from '@utils/store/actions';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import {
  format as dateFnsFormat,
  formatDistanceToNow as dateFnsFormatDistanceToNow,
} from 'date-fns';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
import Icon from '@react-native-vector-icons/material-design-icons';
import {
  IconButton,
} from 'react-native-paper';
import PassageDisplay from '@components/PassageDisplay'; // @TODO: this causes an import cycle because PassageDisplay/Verse can contain a WrinkleBadge. should be okay for now, but a refactor in the future would be nice
import Popover from '@components/common/Popover';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const wrinkleDateFormat = "MMMM d, yyyy 'at' h:mmaaaaa'm'";

const WrinkleBadge = (_p: WrinkleBadgeProps) => {
  const props = {
    ...defaultProps,
    ..._p,
  };

  const [_currWrinkleIndex, setCurrWrinkleIndex] = useState(props.wrinkles?.length - 1);

  const currWrinkle = props.wrinkles?.[_currWrinkleIndex];

  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles, {
    positionRelative: props.positionRelative,
    absoluteTop: props.absoluteTop,
    absoluteRight: props.absoluteRight,
    badgeSize: props.badgeSize,
  }); // @TODO: do these have to be passed in to styles? or as props at all?
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_isWrinkleBadgeOpen, setIsWrinkleBadgeOpen] = useState(false);

  useEffect(() => {
    // allow parent to open wrinkle badge but not close it
    if (props.isOpen) {
      setIsWrinkleBadgeOpen(true);
    }
  }, [props.isOpen]);

  const isTestOpenCurrWrinkle = utilities.isWrinkleTestOpen(currWrinkle);
  const dateLastPassed = !_isWrinkleBadgeOpen ? '' : currWrinkle?.date_last_passed && dateFnsFormat(new Date(currWrinkle.date_last_passed), wrinkleDateFormat, {locale: i18n.dateFnsCode}); // this is user's TZ
  const dateLastPassedHumanReadable = !_isWrinkleBadgeOpen ? '' : currWrinkle?.date_last_passed && dateFnsFormatDistanceToNow(new Date(currWrinkle.date_last_passed), {locale: i18n.dateFnsCode}); // this is user's TZ
  const dateEarned = !_isWrinkleBadgeOpen ? '' : currWrinkle?.date_earned && dateFnsFormat(new Date(currWrinkle.date_earned), wrinkleDateFormat, {locale: i18n.dateFnsCode}); // this is user's TZ
  const dateEarnedHumanReadable = !_isWrinkleBadgeOpen ? '' : currWrinkle?.date_earned && dateFnsFormatDistanceToNow(new Date(currWrinkle.date_earned), {locale: i18n.dateFnsCode}); // this is user's TZ

  const _prettySecondsUntilOpen = hooks.useTimeRemaining(currWrinkle, !_isWrinkleBadgeOpen); // @TODO: if dialog isn't open, no need to calculate this

  const translation = hooks.useTypedSelector((state) => state.translations.find((t) => t.id === currWrinkle?.translation_id));

  const [bookApiId, chapterNumber, verseNumber] = currWrinkle?.verse_api_id.split('.') || [];
  const book = hooks.useTypedSelector((state) => state.books.find((b) => b.api_id === bookApiId));

  const passage = !_isWrinkleBadgeOpen ? null : new CPassage(-1, currWrinkle?.translation_id, book?.id, chapterNumber, verseNumber, verseNumber);

  useEffect(() => {
    // purely for UX...this makes the pressed badge the current one every time the dialog is opened instead of keeping previous state
    setCurrWrinkleIndex(props.wrinkles?.length - 1);
  }, [_isWrinkleBadgeOpen, props.wrinkles?.length]);

  if (currWrinkle?.level_number === 0) { // @NOTE: can't test truthiness because some passages (with multiple verses) have indeterminate status (i.e. no number, just a badge)
    // @TODO: these should never be the case...only wrinkles with a level number >= 1 should be passed to this component
    return null;
  }

  return (
    <View style={styles.container}>
      {props.wrinkles?.map((wrinkle, index) => {
        const isTestOpen = utilities.isWrinkleTestOpen(wrinkle);
        const isWrinkleCompleted = wrinkle?.level_number >= globals.srsLevelWrinkleIsEarnedAt;
        const isOtherTranslation = wrinkle?.translation_id !== props.translationIdForThisVerse;

        return (
          <View
            key={wrinkle.id}
            style={[
              index === props.wrinkles.length - 1
                ? {}
                : {
                  position: 'absolute',
                  top: -(props.wrinkles.length - 1 - index) * 4,
                  right: -(props.wrinkles.length - 1 - index) * 2,
                },
            ]}
          >
            <TouchableOpacity
              style={styles.badgeWrapper}
              disabled={!verseNumber || props.isBadgePressDisabled || (index !== props.wrinkles.length - 1)}
              onPress={() => setIsWrinkleBadgeOpen(true)}
            >
              <View
                style={[
                  styles.badge,
                  isWrinkleCompleted && styles.completedBadge,
                  !isTestOpen && styles.closedBadge,
                  wrinkle?.is_paused && styles.pausedBadge,
                  isOtherTranslation && styles.otherTranslationBadge,
                ]}
              >
                <Text
                  style={[
                    styles.badgeText,
                    isWrinkleCompleted && styles.completedBadgeText,
                    !isTestOpen && styles.closedBadgeText,
                    wrinkle?.is_paused && styles.pausedBadgeText,
                    isOtherTranslation && styles.otherTranslationBadgeText,
                  ]}
                >
                  {props.levelToDisplay || wrinkle?.level_number || ''}
                </Text>
                {!!wrinkle?.is_paused &&
                  <View style={[styles.pauseIconWrapper, isOtherTranslation && styles.otherTranslationPauseBadge]}>
                    <Icon
                      style={[styles.pauseIcon, isOtherTranslation && styles.otherTranslationBadgeIcon]}
                      size={styles.globalScale * 12}
                      name='pause'
                    />
                  </View>
                }
              </View>
            </TouchableOpacity>
          </View>
        );
      })}
      {_isWrinkleBadgeOpen &&
        <Dialog
          visible={_isWrinkleBadgeOpen}
          onDismiss={() => setIsWrinkleBadgeOpen(false)}
        >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
          <Dialog.Content>
            <View style={styles.titleWrapper}>{/* @TODO: eventually, convert to Dialog.Title */}
              <Text style={styles.dialogTitle}>{passage?.prettyPassageName()}</Text>
              <Button
                onPress={props.wrinkles.length <= 1 ? null : () => setCurrWrinkleIndex((curr) => curr === props.wrinkles.length - 1 ? 0 : curr + 1)}
                icon={props.wrinkles.length > 1 ? 'chevron-right' : undefined}
                containerStyle={styles.nextButtonContainer}
                contentStyle={styles.nextButtonContent}
              >
                {translation?.abbreviation}
              </Button>
            </View>
            {passage &&
              <View style={styles.passageWrapper}>
                <PassageDisplay
                  passages={[passage]}
                  hideLicensing={true}
                  showFormatting={false}
                  showFootnotes={false}
                  showHeadings={false}
                  alwaysShowSelectionMenu={false}
                  hideCompare={true}
                  hideAddToGoal={true}
                  hideNumbersTextToggle={true}
                  isPressDisabled={true}
                  showWrinkleBadges={true}
                  isBadgePressDisabled={true}
                  verseTextStyles={styles.verseText}
                />
              </View>
            }
            {!!dateEarned &&
              <View style={styles.metadataRow}>
                <Popover.Wrapper
                  popover={
                    <Popover.Inner>
                      <Popover.Text title={true}>{i18n('dateEarned')}</Popover.Text>
                      <Popover.Text>
                        {i18n('wrinkleEarnedOn', dateEarned)}
                      </Popover.Text>
                      {/* @TODO: add graphic here */}
                    </Popover.Inner>
                  }
                >
                  <Text style={styles.metadataText}><Text style={styles.titleText}>Earned: </Text>{dateEarnedHumanReadable} ago</Text>{/* @TODO: i18n */}
                </Popover.Wrapper>
              </View>
            }
            <View style={styles.metadataRow}>
              <Popover.Wrapper
                popover={
                  <Popover.Inner>
                    <Popover.Text title={true}>{i18n('lastPassed')}</Popover.Text>
                    <Popover.Text>
                      {i18n('lastRecitedOn', dateLastPassed)}
                    </Popover.Text>
                    {/* @TODO: add graphic here */}
                  </Popover.Inner>
                }
              >
                <Text style={styles.metadataText}><Text style={styles.titleText}>Last passed: </Text>{dateLastPassedHumanReadable} ago</Text>{/* @TODO: i18n */}
              </Popover.Wrapper>
            </View>
            <View style={styles.metadataRow}>
              <Popover.Wrapper
                popover={
                  <Popover.Inner>
                    <Popover.Text title={true}>{i18n('pausedWrinkles')}</Popover.Text>
                    <Popover.Text>
                      This wrinkle is currently <Text style={[styles.pauseNote, styles.pausedStatus]}>{currWrinkle?.is_paused ? 'paused' : 'active'}</Text>.  Paused wrinkles are hidden from the Wrinkle Level Up widget.  {currWrinkle.is_paused ? 'Unpause' : 'Pause'} this wrinkle by pressing the {currWrinkle.is_paused ? 'play' : 'pause'} button below.{/* @TODO: i18n */}
                    </Popover.Text>{/* @TODO: i18n */}
                    {/* @TODO: add graphic here */}
                  </Popover.Inner>
                }
              >
                <Text style={styles.metadataText}><Text style={styles.titleText}>Status: </Text>{currWrinkle?.is_paused ? 'paused' : 'active'}</Text>{/* @TODO: i18n */}
              </Popover.Wrapper>
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <IconButton
              style={styles.actionIconButton}
              mode='outlined'
              icon={currWrinkle?.is_paused ? 'play' : 'pause'}
              iconColor={styles.isDarkMode ? styles.colors.white : styles.colors.primary}
              size={styles.globalScale * 18}
              onPress={() => dispatch(updateWrinkle(currWrinkle?.id, !currWrinkle?.is_paused))}
            />
            <View style={styles.spacer} />
            <IconButton
              style={styles.actionIconButton}
              mode='contained'
              containerColor={styles.colors.primary}
              icon='book'
              iconColor={styles.isDarkMode ? styles.colors.white : styles.colors.primaryExtraLight}
              size={styles.globalScale * 18}
              onPress={() => {
                setIsWrinkleBadgeOpen(false);
                const chapterId = CPassage.getChapterByBookIdAndNumber(book?.id, +chapterNumber).id;
                dispatch(addToReadingHistory(chapterId));
                navigation.navigate(READ_ROUTE, {
                  chapterId,
                  contextPassage: new CPassage(
                    -1,
                    translation?.id,
                    book?.id,
                    chapterNumber,
                    verseNumber,
                    verseNumber,
                  ),
                });
              }}
            />
            <Button
              uppercase={false}
              containerStyle={styles.testNowButton}
              mode='contained'
              theme={isTestOpenCurrWrinkle ? styles.colors.accent : styles.colors.lightGray}
              onPress={() => {
                setIsWrinkleBadgeOpen(false);
                navigation.navigate(PRACTICE_ROUTE, {
                  noGoal: true, // prevent translation override for the current goal to override the currently selected translation // @TODO: there could maybe be a cleaner way to do this
                  passages: [passage],
                  isTest: true,
                });
              }}
            >
              {isTestOpenCurrWrinkle ? i18n('testNow') : _prettySecondsUntilOpen}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </View>
  );
};

class WrinkleBadgeProps {

  levelToDisplay?: number; // @TODO: required with status
  wrinkles?: any[]; // @TODO: why isn't this required? (because of the badge on the test-tube instead of the verse...this is a problem)
  translationIdForThisVerse?: number;
  isOpen?: boolean;
  positionRelative?: boolean;
  absoluteTop?: number;
  absoluteRight?: number;
  badgeSize?: number = 20;
  isBadgePressDisabled?: boolean;
  onlyShowOne?: boolean;
};
const defaultProps = new WrinkleBadgeProps();

export default WrinkleBadge;
